import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  Calculator, 
  Users, 
  FolderOpen, 
  FileText,
  Receipt,
  MessageSquare,
  Mail,
  FileCode,
  Bot,
  ClipboardList,
  Scale,
  UserCog,
  FileSpreadsheet,
  TrendingUp,
  Wallet,
  Zap,
  Sparkles
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { automatizacionAPI } from '../../api';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Mapeo de items del menú con sus IDs de permisos
// moduleId = identificador unico para el kill-switch global (v41 admin config)
const menuItems = [
  { icon: LayoutDashboard, label: 'Panel', path: '/panel', permisoId: 'panel', moduleId: 'panel' },
  { icon: Calendar, label: 'Calendario', path: '/calendario', permisoId: 'calendario', moduleId: 'calendario' },
  { icon: Users, label: 'Contactos', path: '/contactos', permisoId: 'contactos', moduleId: 'contactos' },
  { icon: FolderOpen, label: 'Expedientes', path: '/expedientes', permisoId: 'expedientes', moduleId: 'expedientes' },
  { icon: Scale, label: 'Sentencias', path: '/sentencias', permisoId: 'expedientes', moduleId: 'sentencias' },
  { icon: FileSpreadsheet, label: 'Auditoría', path: '/auditoria-expedientes', permisoId: 'auditoria-expedientes', moduleId: 'auditoria-expedientes' },
  { icon: Sparkles, label: 'Auditoría IA', path: '/expedientes/auditoria-ia', permisoId: 'expedientes', moduleId: 'auditoria-ia' },
  { icon: ClipboardList, label: 'Tareas', path: '/tareas', permisoId: 'expedientes', moduleId: 'tareas' },
  { icon: UserCog, label: 'RRHH', path: '/rrhh', permisoId: 'rrhh', moduleId: 'rrhh', adminOnly: true },
  { icon: Bot, label: 'Asistente IA', path: '/chat', permisoId: 'asistente-ia', moduleId: 'chat' },
  { icon: FileText, label: 'Informes', path: '/informes', permisoId: 'informes', moduleId: 'informes' },
  { icon: MessageSquare, label: 'Comunicaciones', path: '/comunicaciones', permisoId: 'comunicaciones', moduleId: 'comunicaciones' },
  { icon: Receipt, label: 'Contabilidad', path: '/contabilidad', permisoId: 'contabilidad', moduleId: 'contabilidad' },
  { icon: Mail, label: 'Correo', path: '/correo', permisoId: 'correo', moduleId: 'correo' },
  { icon: FileCode, label: 'Plantillas', path: '/plantillas', permisoId: 'plantillas', moduleId: 'plantillas' },
  { icon: Calculator, label: 'Calculadoras', path: '/calculadora', permisoId: 'calculadoras', moduleId: 'calculadora' },
  { icon: Zap, label: 'Automatización', path: '/automatizacion', permisoId: 'automatizacion', moduleId: 'automatizacion' }
];

// Función para verificar si un módulo es visible según permisos
const hasPermission = (permisos, permisoId) => {
  // Si no hay permisos definidos, mostrar todo (admin por defecto)
  if (!permisos || Object.keys(permisos).length === 0) {
    return true;
  }
  
  const permiso = permisos[permisoId];
  
  // Si el permiso no está definido, mostrar el módulo
  if (permiso === undefined) {
    return true;
  }
  
  // Verificar lectura
  if (typeof permiso === 'object') {
    return permiso.lectura === true;
  }
  
  return !!permiso;
};

const TopNavigation = () => {
  const location = useLocation();
  const [permisos, setPermisos] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  // v41: lista global de modulos deshabilitados por admin
  const [modulosDeshabilitados, setModulosDeshabilitados] = useState([]);

  useEffect(() => {
    try {
      const userData = localStorage.getItem('lexduo_user');
      if (userData) {
        const user = JSON.parse(userData);
        setPermisos(user.permisos || {});
        setIsAdmin(user.rol === 'admin' || user.email === 'admin@lexduo.cat');
      }
    } catch (error) {
      console.error('Error cargando permisos:', error);
    }
  }, []);

  // v41: cargar modulos deshabilitados globalmente
  useEffect(() => {
    const token = localStorage.getItem('lexduo_token');
    if (!token) return;
    axios.get(`${API}/api/config/modulos-menu`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setModulosDeshabilitados(res.data?.deshabilitados || []))
      .catch(() => setModulosDeshabilitados([]));
  }, []);

  // Polling de notificaciones de automatización
  const loadPendingCount = useCallback(async () => {
    try {
      const res = await automatizacionAPI.getPendingCount();
      setPendingCount(res.data.total_accion || 0);
    } catch {
      // Silenciar errores (no auth, etc)
    }
  }, []);

  useEffect(() => {
    loadPendingCount();
    const interval = setInterval(() => {
      if (!document.hidden) loadPendingCount();
    }, 60000);
    const onVisibility = () => { if (!document.hidden) loadPendingCount(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadPendingCount]);

  // Filtrar items del menú según permisos (admins ven todo) y kill-switch global v41
  const visibleByPerm = isAdmin
    ? menuItems
    : menuItems.filter(item => !item.adminOnly && hasPermission(permisos, item.permisoId));
  // v41: aplicar el kill-switch global TAMBIEN al admin (admin accede a Configuracion por sidebar)
  const visibleItems = visibleByPerm.filter(item => !modulosDeshabilitados.includes(item.moduleId));

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="flex items-center overflow-x-auto">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
                          (item.path !== '/panel' && location.pathname.startsWith(item.path));
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 hover:text-blue-700 hover:border-blue-300",
                isActive 
                  ? "text-blue-700 border-blue-600" 
                  : "text-gray-600 border-transparent"
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{item.label}</span>
              {item.path === '/automatizacion' && pendingCount > 0 && (
                <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {pendingCount > 99 ? '99+' : pendingCount}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default TopNavigation;
